import React, { useEffect } from 'react';
import PropTypes from 'prop-types'; // Recommended for defining prop types
import Modal from 'react-modal';
import styles from './FollowUpModal.module.css'; // Your CSS Module

/**
 * FollowUpModal Component
 *
 * A modal dialog displayed after an email is sent, prompting the user
 * to optionally create a follow-up task. Uses react-modal for accessibility.
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Controls whether the modal is visible.
 * @param {Function} props.onRequestClose - Function called when the modal requests to be closed (e.g., Esc key, overlay click, Cancel button).
 * @param {string} props.prompt - The suggested text for the follow-up task.
 * @param {Function} props.onPromptChange - Handler for changes to the task text textarea.
 * @param {string|number} props.days - The number of days for the follow-up delay (input value, can be string).
 * @param {Function} props.onDaysChange - Handler for changes to the days input field.
 * @param {Function} props.onConfirm - Function called when the user confirms the action (creates task if days > 0).
 * @param {boolean} [props.isSubmitting=false] - Optional flag to disable inputs/buttons during submission.
 */
const FollowUpModal = ({
    isOpen,
    onRequestClose,
    prompt,
    onPromptChange,
    days,
    onDaysChange,
    onConfirm,
    isSubmitting = false // Added optional submitting state
}) => {

    // Effect to potentially focus the first input when the modal opens
    // react-modal usually handles this, but explicit focus can sometimes be desired.
    const textAreaRef = React.useRef(null);
    useEffect(() => {
        if (isOpen && textAreaRef.current) {
            // Small delay might be needed for modal transition
            // setTimeout(() => textAreaRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Determine button text based on days input
    const parsedDays = parseInt(days, 10);
    const isCreatingTask = !isNaN(parsedDays) && parsedDays > 0;
    const confirmButtonText = isCreatingTask ? 'Create Task & Close' : 'Close (No Task)';

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose} // Handles Esc key and overlay clicks
            className={styles.modalContent} // Apply styles to the modal content container
            overlayClassName={styles.modalOverlay} // Apply styles to the overlay background
            contentLabel="Create Optional Follow-up Task" // Essential for screen readers
            shouldCloseOnOverlayClick={!isSubmitting} // Prevent closing during submit
            shouldCloseOnEsc={!isSubmitting} // Prevent closing during submit
            aria={{
                modal: 'true', // Explicitly set aria-modal
                // aria-labelledby could point to the h2 ID if needed
                // aria-describedby could point to the p ID if needed
            }}
            // Optional: Add portalClassName if you need to style the portal container itself
            // portalClassName="your-portal-class"
        >
            {/* Modal Content Structure */}
            <h2 id="followUpModalTitle">Create Optional Follow-up Task?</h2>
            <p id="followUpModalDescription">
                An email was sent successfully. Would you like to schedule a follow-up task in your To-Do list?
            </p>

            <div className={styles.formGroup}>
                <label htmlFor="followUpPrompt">Follow-up Task Text:</label>
                <textarea
                    ref={textAreaRef} // Ref for potential focus management
                    id="followUpPrompt"
                    value={prompt}
                    onChange={onPromptChange}
                    rows="3"
                    disabled={isSubmitting}
                    aria-describedby="followUpPromptHint" // Example hint if needed
                />
                {/* <span id="followUpPromptHint" style={{ fontSize: '0.8em', color: '#aaa' }}>
                    This text will appear in your To-Do list.
                </span> */}
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="followUpDays">Days until follow-up (0 for none):</label>
                <input
                    type="number"
                    id="followUpDays"
                    value={days}
                    onChange={onDaysChange}
                    min="0"
                    placeholder="e.g., 7"
                    disabled={isSubmitting}
                    style={{ width: '80px' }} // Keep input reasonably sized
                />
            </div>

            {/* Action Buttons */}
            <div className={styles.modalActions}>
                 {/* Confirm Button */}
                 <button
                    onClick={onConfirm}
                    className={styles.confirmButton}
                    disabled={isSubmitting || !prompt.trim()} // Disable if submitting or prompt is empty
                 >
                    {isSubmitting ? 'Saving...' : confirmButtonText}
                 </button>

                 {/* Cancel Button - Uses onRequestClose */}
                 <button
                    onClick={onRequestClose}
                    className={styles.cancelButton}
                    disabled={isSubmitting} // Disable during submission
                 >
                    Cancel
                 </button>
            </div>
        </Modal>
    );
};

// Define PropTypes for type checking and documentation
FollowUpModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    prompt: PropTypes.string.isRequired,
    onPromptChange: PropTypes.func.isRequired,
    days: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onDaysChange: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool,
};

// Remember to set the app element in your main application file (e.g., App.js or index.js)
// import Modal from 'react-modal';
// Modal.setAppElement('#root'); // Or your app's root element ID

export default FollowUpModal;